import { ModelElementType } from "@/components/entities";
import {
  TableDeletionAction,
  TransferAction,
} from "./deletion/DeleteModelDialogTypes";
import ModelDeletionAnalysis, {
  TableUsageStatus,
  TrashModel,
} from "target_wasm";

export type TableIdSet = Set<string>;
export type TableAction = TableDeletionAction | TransferAction;

export interface DeleteModelState {
  loading: boolean;
  error: string | null;
  deleting: boolean;
  transferringTableId: string | null;
  transferSuccessTableIds: TableIdSet;
  fadingOutTableIds: TableIdSet;
  transferErrorTableIds: TableIdSet;
  fadingOutErrorTableIds: TableIdSet;
  activeBatchTransferIds: TableIdSet;
  removedTableIds: TableIdSet;
  modelSearchQuery: string;
  analysis: typeof ModelDeletionAnalysis | null;
  tableActions: Record<string, TableAction>;
  allAvailableModels: ModelElementType[];
  expandedSections: { critical: boolean; moderate: boolean; low: boolean };
  selectedTables: TableIdSet;
  activeId: string | null;
  dragOverModelId: string | null;
  dragOverTrash: boolean;
  dragOverTransferSection: boolean;
}

const DEFAULT_TRASH_MODEL_VALUES: Omit<
  ModelElementType,
  "model_id" | "name" | "color" | "icon" | "scheduled_deletion_date"
> = {
  created_at: 0,
  last_modified_at: 0,
  state_entity: [],
  version: "",
  relation: "Owner",
  owner_id: "",
  active_sessions: [],
  last_opened: 0,
  team_access: "",
  user_access: [],
};

export const trashModelToModelElement = (
  model: TrashModel | null,
): ModelElementType | null => {
  if (!model) return null;

  return {
    ...DEFAULT_TRASH_MODEL_VALUES,
    model_id: model.model_id_js,
    name: model.model_name_js || "Unnamed Model",
    color: model.color_js,
    icon: model.icon_js,
    scheduled_deletion_date: model.scheduled_deletion_date_js
      ? Number(model.scheduled_deletion_date_js)
      : undefined,
  };
};

export enum ModelSectionMode {
  Overview,
  Trash,
}

/** Who may rename a model and change its appearance. A catalog model has no
 *  owner — every catalog manager arrives as a Collaborator — so mirror the
 *  server's `can_edit` (collaborator or catalog manager) instead of using Owner
 *  as a proxy for it. Personal models stay owner-only. Delete, share, and team
 *  access remain Owner-gated and must NOT use this. */
export const canEditModelMeta = (
  model: Pick<ModelElementType, "relation" | "is_catalog">,
): boolean =>
  model.is_catalog
    ? model.relation === "Collaborator" || model.relation === "Owner"
    : model.relation === "Owner";

/** Sort options shown in the Models overview header. */
export type ModelSortKey = "last_opened_by_me" | "last_modified" | "created";

export const MODEL_SORT_LABELS: Record<ModelSortKey, string> = {
  last_opened_by_me: "Last opened by me",
  last_modified: "Last modified",
  created: "Last created",
};

export const DEFAULT_MODEL_SORT: ModelSortKey = "last_opened_by_me";

/** Stable, in-place sort. Returns a new array; never mutates input.
 *  Starred models always sort before non-starred, then by the chosen key. */
export function sortModels<
  T extends {
    last_opened_by_me?: number;
    last_modified_at?: number;
    created_at?: number;
    is_starred?: boolean;
  },
>(models: T[], key: ModelSortKey): T[] {
  const valueOf = (m: T): number => {
    switch (key) {
      case "last_opened_by_me":
        return m.last_opened_by_me ?? 0;
      case "last_modified":
        return m.last_modified_at ?? 0;
      case "created":
        return m.created_at ?? 0;
    }
  };
  return [...models].sort((a, b) => {
    const aStar = a.is_starred ? 1 : 0;
    const bStar = b.is_starred ? 1 : 0;
    if (aStar !== bStar) return bStar - aStar;
    return valueOf(b) - valueOf(a);
  });
}

/** Stable, non-mutating sort: pinned (starred) models first, then the rest
 *  alphabetically by name (case-insensitive). Used by the team models list. */
export function sortModelsPinnedThenName<
  T extends { name?: string; is_starred?: boolean },
>(models: T[]): T[] {
  return [...models].sort((a, b) => {
    const aStar = a.is_starred ? 1 : 0;
    const bStar = b.is_starred ? 1 : 0;
    if (aStar !== bStar) return bStar - aStar;
    return (a.name || "Unnamed Model").localeCompare(
      b.name || "Unnamed Model",
      undefined,
      { sensitivity: "base" },
    );
  });
}

/// Highest deletion risk among a trashed model's scheduled tables, as a text
/// colour class. Critical means another user's model imports the table, so
/// letting the timer run loses data for someone else.
export function trashRiskColor(trashModel: TrashModel | null): string {
  const tables = trashModel?.tables_js ?? [];
  if (tables.some((t) => t.usage_status_js === TableUsageStatus.Critical)) {
    return "text-error";
  }
  if (tables.some((t) => t.usage_status_js === TableUsageStatus.Moderate)) {
    return "text-warning";
  }
  return "text-fg-muted-medium";
}
